import { MoreHorizontal } from 'lucide-react'

import type { OrderActionItem } from '@/components/sections/order-actions'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export interface OrderActionGroup {
    key: string
    /** Nhãn nhóm hiện trong menu; bỏ trống thì nhóm không có tiêu đề. */
    label?: string
    items: OrderActionItem[]
}

interface Props {
    groups: OrderActionGroup[]
    /** Nhãn a11y cho nút kebab (screen reader đọc thay icon). */
    label: string
}

/**
 * Nút kebab (…) gom mọi hành động phụ của đơn vào một menu.
 *
 * Dùng cho mobile: header chỉ còn 1 nút hành động chính + nút này, thay vì 4
 * hàng nút trải dài đẩy nội dung đơn xuống dưới màn hình.
 *
 * Menu chỉ phát lệnh — mọi dialog do component cha render, vì Radix gỡ nội dung
 * menu khi đóng và sẽ gỡ luôn dialog đặt bên trong.
 */
export function OrderActionMenu({ groups, label }: Props) {
    const visible = groups.filter((g) => g.items.length > 0)
    if (visible.length === 0) return null

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label={label}>
                    <MoreHorizontal className="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                {visible.map((group, index) => (
                    <div key={group.key}>
                        {index > 0 && <DropdownMenuSeparator />}
                        {group.label && (
                            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
                        )}
                        {group.items.map((item) => (
                            <DropdownMenuItem
                                key={item.key}
                                disabled={item.disabled}
                                aria-disabled={item.muted || undefined}
                                className={
                                    item.muted ? 'opacity-50' : undefined
                                }
                                title={item.title}
                                variant={
                                    item.variant === 'destructive'
                                        ? 'destructive'
                                        : 'default'
                                }
                                onSelect={item.onSelect}
                            >
                                {item.icon}
                                {item.label}
                            </DropdownMenuItem>
                        ))}
                    </div>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
